// GuardedRoute.js
// A permission-checking wrapper for React Router v6 routes.
// Used as the element inside a <Route>: <Route path='/x' element={<GuardedRoute ...><Screen /></GuardedRoute>} />

import React from 'react';
import { Navigate } from 'react-router-dom';
import { useSelector } from 'react-redux';

/**
 * GuardedRoute - Enforces permission/role-based access control for React Router v6.
 *
 * Usage:
 * <Route
 *   path="/business-group/edit/:id"
 *   element={
 *     <GuardedRoute
 *       blockedTypesRoles={[userTypeEnum.OperationManager]}
 *       blockedPermissions={[userPermissionsEnum.View]}
 *     >
 *       <AddOrEditBusinessGroupScreen />
 *     </GuardedRoute>
 *   }
 * />
 *
 * @param {Array}  blockedTypesRoles  - User types that are blocked (e.g. ['OperationManager'])
 * @param {Array}  blockedPermissions - Permissions that are blocked (e.g. ['View'])
 * @param {string} redirectTo         - Redirect target when blocked (default: '/payments')
 * @param {node}   children           - The screen element to render when access is allowed
 */
const GuardedRoute = ({
    blockedTypesRoles = [],
    blockedPermissions = [],
    redirectTo = '/payments',
    children,
}) => {
    const { userInfo } = useSelector((state) => state.userInfoReducer);

    const isBlockedRole = blockedTypesRoles.length > 0 && blockedTypesRoles.includes(userInfo?.userType);
    const isBlockedPermission = blockedPermissions.length > 0 && blockedPermissions.includes(userInfo?.permissions);
    const isBlocked = isBlockedRole && isBlockedPermission;

    if (isBlocked) {
        return <Navigate to={redirectTo} replace />;
    }

    return children;
};

export default GuardedRoute;

